import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {
  getAccessibleOrInaccessibleStorableId,
  GridType,
  isInaccessibleStorable,
  GridCell,
  MinimalContainer,
} from 'constants/SampleConstants';
import StorableChip from 'inventory/chips/StorableChip';
import {FillDirection} from 'lookup-config/LookupConfigModalConstants';
import notifications from 'notifications';
import {NOT_SPECIFIED} from 'samples/grids/GridCellTable';
import GridPosition from 'samples/grids/GridPosition';
import {ScanToMoveGridCell, ScanToMoveCellByCoordinates} from 'samples/grids/ScanToMoveGridUtil';
import * as SampleUtil from 'samples/SampleUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList, {getChipsRowHeight} from 'ui-library/chips/ChipList';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ClickableIconStyle} from 'ui-library/icons/ClickableIcon';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_KEYS,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';
import {Optional} from 'util/Types';

const MAX_VISIBLE_CONTAINED_ITEMS = 3;

export interface ScanToMoveGridTableProps {
  /** Mapping from containers to coordinates. This includes both the existing containers (marked with
   * `isPreexisting` set to true) and new ones being scanned in. Coordinates of the form A1, B2, etc.
   * Should probably be initialized by the caller using useInitialContainersByCoordinatesMapping. */
  containersByCoordinates: ScanToMoveCellByCoordinates;
  /** Function to update mapping from coordinates to scanned containers. This is called with the changes to
   * scanned containers. Parent is responsible for merging state. */
  updateContainersForCoordinates: (newContainersMapping: ScanToMoveCellByCoordinates) => void;
  /** Dimensions of the grid. */
  gridDimensions: {width: number; height: number};
  /** Type of the grid. Controls how grid position is displayed in the table. */
  gridType: GridType;
  /** Coordinates that have focus for scanning. */
  selectedCoordinates?: string | null;
  /** Select different coordinates to scan into. */
  setSelectedCoordinates: (coordinates: string) => void;
}

interface ScanToMoveGridRowData {
  coordinates: JSX.Element;
  container: JSX.Element;
  barcode: string | null;
  contents: JSX.Element | null;
}

interface ScanToMoveGridRow extends ScrollableTableRow {
  data: ScanToMoveGridRowData;
  extra: {cell: ScanToMoveGridCell};
}

/**
 * Create a mapping from gridCoordinates to grid cells with initial containers.
 *
 * Maps all existing containers in the grids, and has empty grid cells with `isPreexisting` marked to false
 * for the coordinates that can have new items scanned in.
 *
 * @param gridCellArrays Doubly nested array of grid's cells. Same shape as grid.cells
 * @param fillDirection What direction grid will be filled; used to return available coordinates in order
 *
 * @returns {{containersByCoordinates, orderedAvailableCoordinates}} Mapping of coordinates to containers, and a list
 * of the coordinates that do not have preexisting containers (and can be scanned into)
 */
export function useInitialContainersByCoordinatesMapping(
  gridCellArrays: Array<Array<GridCell | null>>,
  fillDirection: FillDirection = FillDirection.ACROSS_ROWS
): {
  containersByCoordinates: ScanToMoveCellByCoordinates;
  orderedAvailableCoordinates: Array<string>;
} {
  const width = gridCellArrays[0].length;
  const height = gridCellArrays.length;
  return useMemo(() => {
    const getCellAtIndex = (rowIndex: number, columnIndex: number): ScanToMoveGridCell =>
      gridCellArrays[rowIndex][columnIndex] != null
        ? {
            ...gridCellArrays[rowIndex][columnIndex]!,
            isPreexisting: gridCellArrays[rowIndex][columnIndex]!.container != null,
          }
        : {rowIndex, columnIndex, isPreexisting: false};

    // Build the mapping in the desired order, either A1-A12, B1-B12 etc. (across rows) or A1,B1,...A2,B2...
    // (down columns). Which index corresponds to the row & which to the column depends on this direction too
    const {outerDimension, innerDimension} =
      fillDirection === FillDirection.ACROSS_ROWS
        ? {outerDimension: height, innerDimension: width}
        : {outerDimension: width, innerDimension: height};
    const getRowIndex = (outerIdx: number, innerIdx: number): number =>
      fillDirection === FillDirection.ACROSS_ROWS ? outerIdx : innerIdx;
    const getColIndex = (outerIdx: number, innerIdx: number): number =>
      fillDirection === FillDirection.ACROSS_ROWS ? innerIdx : outerIdx;

    const containersByCoordinates = objectFromEntries(
      [...Array(outerDimension)].flatMap((_, outerIdx: number) =>
        [...Array(innerDimension)].map(
          (_, innerIdx: number) =>
            [
              SampleUtil.formatGridCoordinates(
                getRowIndex(outerIdx, innerIdx),
                getColIndex(outerIdx, innerIdx)
              ),
              getCellAtIndex(getRowIndex(outerIdx, innerIdx), getColIndex(outerIdx, innerIdx)),
            ] as [string, ScanToMoveGridCell]
        )
      )
    );
    const orderedAvailableCoordinates = Object.entries(containersByCoordinates)
      .filter(([_, cell]) => cell.container == null)
      .map(([coordinates, _]) => coordinates);
    return {containersByCoordinates, orderedAvailableCoordinates};
  }, [width, height, gridCellArrays, fillDirection]);
}

export default function ScanToMoveGridTable(props: ScanToMoveGridTableProps): JSX.Element {
  const {containersByCoordinates} = props;
  const cells = useMemo(() => Object.values(containersByCoordinates), [containersByCoordinates]);

  // "Selecting" rows can only be done for scanned rows, and means deleting them
  const handleDeleteScannedContainers = (rows: Array<ScanToMoveGridRow>, toDelete: boolean): void => {
    // toDelete is passed as true when a row is "selected"
    // For this table, toDelete should not be false, but this does still somehow get called with toDelete set
    // to false. We want to simply no-op in that case
    if (toDelete) {
      const deletedCoordinates = rows.map((row) => row.id);
      const deletedPreexistingContainers = rows
        .filter((row) => row.extra.cell.originalGridCoordinates)
        .map((row) => row.extra.cell);
      const modifiedContainers: ScanToMoveCellByCoordinates = {};
      let message: string | null = null;

      // for pre-existing containers, return them to their original positions
      for (const cell of deletedPreexistingContainers) {
        const originalCoordinates = cell.originalGridCoordinates;
        if (originalCoordinates != null) {
          const containerAtOriginalCoordinates = props.containersByCoordinates[originalCoordinates].container;
          if (containerAtOriginalCoordinates != null) {
            // Only accessible containers can be deleted or moved, so both original & replacement container at
            // the coordinates are guaranteed to be accessible
            message = `${
              (cell.container as MinimalContainer).barcode
            } returned to position ${originalCoordinates}, replacing ${
              (containerAtOriginalCoordinates as MinimalContainer).barcode
            }.`;
          }
          modifiedContainers[originalCoordinates] = {
            ...props.containersByCoordinates[originalCoordinates],
            container: cell.container,
            isPreexisting: true,
            originalGridCoordinates: null,
          };
        }
      }

      // clear the rows of deleted containers
      const deletedContainers = objectFromEntries(
        deletedCoordinates.map(
          (coordinates) =>
            [
              coordinates,
              {
                ...props.containersByCoordinates[coordinates],
                isPreexisting: false,
                container: null,
                originalGridCoordinates: null,
              },
            ] as [string, ScanToMoveGridCell]
        )
      );

      props.updateContainersForCoordinates({...modifiedContainers, ...deletedContainers});
      notifications.success(message ?? 'Scan removed successfully.', {duration: 7});
    }
  };

  // Clicking a row means moving scanning focus to that row. Only relevant for rows that do not have containers
  const handleRowClick = (row: ScanToMoveGridRow): void => {
    if (row.extra.cell.container != null) {
      return;
    }
    props.setSelectedCoordinates(row.id);
  };

  const getRowContentHeight = (tableRowIndex: number): number => {
    if (tableRowIndex >= cells.length) {
      return TABLE_ROW_BODY_HEIGHT;
    }
    const container = cells[tableRowIndex]?.container;
    const maxNumContentsLinks =
      container == null || isInaccessibleStorable(container) ? 1 : container.contents.length;
    // Return max height after considering: contents chips & container chips
    // Increment MAX_VISIBLE_CONTAINED_ITEMS by 1 because we need space for "Load more" chip to render in the row
    return Math.max(
      getChipsRowHeight(Math.min(maxNumContentsLinks, MAX_VISIBLE_CONTAINED_ITEMS + 1), ChipSize.SMALL),
      getChipsRowHeight(1, ChipSize.SMALL)
    );
  };

  const rows: Array<ScanToMoveGridRow> = Object.entries(props.containersByCoordinates).map(
    ([gridCoordinates, cell]): ScanToMoveGridRow => {
      const {rowIndex, columnIndex, container, isPreexisting} = cell;
      const isPreexistingInCurrentPosition = isPreexisting && cell.originalGridCoordinates == null;

      // Coerce to container or null for easier access to properties
      const accessibleContainer = container != null && isInaccessibleStorable(container) ? null : container;
      const data: ScanToMoveGridRowData = {
        coordinates: (
          <GridPosition
            rowIndex={rowIndex}
            columnIndex={columnIndex}
            gridType={props.gridType}
            gridWidth={props.gridDimensions.width}
          />
        ),
        container:
          container == null ? (
            <span className="u-faded">{NOT_SPECIFIED}</span>
          ) : (
            <div>
              <StorableChip id={getAccessibleOrInaccessibleStorableId(container)} size={ChipSize.SMALL} />
            </div>
          ),
        barcode: accessibleContainer?.barcode ?? null,
        contents:
          accessibleContainer != null ? (
            <ChipList
              itemIds={accessibleContainer.contents.map((content) => content.containedItemId)}
              chipSize={ChipSize.SMALL}
              maxChips={MAX_VISIBLE_CONTAINED_ITEMS}
            />
          ) : null,
      };
      const rowOnlyClassNames = [];
      if (gridCoordinates === props.selectedCoordinates) {
        rowOnlyClassNames.push('focused');
      }
      if (isPreexistingInCurrentPosition) {
        rowOnlyClassNames.push('selectorDisabled', 'cursorDisabled');
      }
      return {
        id: gridCoordinates,
        data,
        extra: {cell},
        icon:
          gridCoordinates === props.selectedCoordinates ? (
            <BenchlingIcon icon="scan" />
          ) : (
            <BenchlingIcon className="scrollableTable-cell-icon" icon="vial" />
          ),
        isSelectorPresent: !isPreexistingInCurrentPosition && container != null,
        selectorIconProps: {icon: 'trash-can', iconStyle: ClickableIconStyle.NEGATIVE_RED},
        rowOnlyClassName: classNames(rowOnlyClassNames),
      };
    }
  );

  function handleScrollToRow(currentSelectedCoordinates: Optional<string>): number | null {
    return currentSelectedCoordinates ? rows.findIndex((row) => row.id === currentSelectedCoordinates) : null;
  }

  const columns: Array<ScrollableTableColumn> = [
    {
      header: 'Position',
      accessorKey: 'coordinates',
      isCollapsible: false,
      isSortable: false,
      width: 120, // Wide enough to display both the position and the coordinates
      isFixed: true,
    },
    {
      header: 'Container',
      accessorKey: 'container',
      flexGrow: 1,
      isCollapsible: false,
      isSortable: false,
    },
    {
      header: 'Barcode',
      accessorKey: 'barcode',
      flexGrow: 1,
      isCollapsible: false,
      isSortable: false,
    },
    {
      header: 'Contents',
      accessorKey: 'contents',
      flexGrow: 1,
      isCollapsible: false,
      isSortable: false,
    },
  ];

  return (
    <div className="u-height-full">
      <ScrollableTable
        className={css(styles.tableContainer)}
        matchParentHeight={true}
        scrollToRow={handleScrollToRow(props.selectedCoordinates) ?? undefined}
        canResizeColumns={true}
        isCompact={true}
        rowSelectionMode={RowSelectionMode.SINGLE}
        disableSelection={false}
        columns={columns}
        rows={rows}
        onRowClick={handleRowClick}
        onRowsSelect={handleDeleteScannedContainers}
        tableKey={TABLE_KEYS.SCAN_TO_MOVE_GRID_TABLE}
        getRowContentHeight={getRowContentHeight}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  tableContainer: {
    minHeight: '100%',
  },
});
